<script setup lang="ts">
import {
  onBeforeMount,
  computed,
  ref,
  watch,
} from 'vue';

import {useRoute} from 'vue-router';
import {pagesPath} from '@/ts/router/pages-path';
import {useLoadingCb} from '@/ts/composables/pure/use-loading-data-ref';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useApi} from '@/ts/composables/stateful/use-api';
import LoadingError from '@/vue/templates/loading-error.vue';
import {useUserStore} from '@/ts/store/user-store';

import GlobalPageTemplate from '@/vue/templates/global-page-template.vue';
import type {NavigationItem} from '@/ts/types/component/navigation';
import {useUserSidebarMenu} from '@/ts/composables/stateful/use-balm-sidebar-menu';
import AnalyticsAnonymizedConsentModal from '@/vue/organisms/analytics-anonymized-consent-modal.vue';
import IfuUpdateModal from '@/vue/organisms/ifu-update-modal.vue';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';
import type {UserResponse} from '@/ts/types/dto/user-dto';
import {useLanguageChange} from '@/ts/composables/stateful/use-language-change';
import {isContractExpired, useContractExpiryWatch} from '@/ts/composables/stateful/use-contract-expiry-guard';
import {isLegalConsentPending} from '@/ts/composables/pure/use-legal-consent-gate';
import OnboardingTour from '@/vue/organisms/tourguide/onboarding-tour.vue';
import SnackbarHost from '@/vue/organisms/snackbar-host.vue';
import {ColorScheme, useUserPreferencesStore} from '@/ts/store/user-preferences.store';

const api = useApi();
const route = useRoute();
const userStore = useUserStore();
const userPreferencesStore = useUserPreferencesStore();
const {balmSidebarMenuItems} = useUserSidebarMenu();
// Reactive backstop for contract-expiry locking: the router beforeEach guard
// blocks locked routes before mount, but can't see the first hard-load (store
// empty until /me resolves) or a mid-session expiry — this watch covers those
// (IPD-2661).
const {isContractExpired: contractExpired} = useContractExpiryWatch();
const showAnalyticsAnonymizedModal = ref(false);
const showIfuUpdateModal = ref(false);

/**
 * Whether the medical device consent gate is still owed.
 *
 * `hasPendingConsents` covers the re-approval case, but not a missing or declined record, and the
 * interventions list raises that gate itself on mount — so without this the IFU notice and the
 * consent gate can be open at once on `/paths` (IPD-3216).
 */
function deviceConsentPending(user: UserResponse | null | undefined): boolean {
  const consent = user?.consents?.medicalDeviceConsent;
  return !consent || !consent.approved || Boolean(consent.needsReapproval);
}
const {canAccessExplore, canAccessHome} = useFeatureAccess();
const {syncBackendLanguage} = useLanguageChange();
const logger = useLogger('MainBasePage');
// True between the moment syncBackendLanguage queues a window.location.replace
// and the browser actually processing it. OR'd with meLoading on the template
// so loading-error's spinner stays up across the meLoading→false transition,
// masking the empty/null-userData paint that would otherwise show on the
// redirect path before the page unloads (IPD-2614).
const isRedirecting = ref(false);

const mainNavigationItems = computed<NavigationItem[]>(() => {
  const items: NavigationItem[] = [];

  // Only add Home tab if user has access
  if (canAccessHome.value) {
    items.push({
      path: pagesPath.main.home,
      labelKey: 'home',
      id: 'home',
    });
  }

  // Only add explore tab if user has access
  if (canAccessExplore.value) {
    items.push({
      path: pagesPath.main.explore,
      labelKey: 'recco_explore',
      id: 'recco_explore',
    });
  }

  items.push({
    path: pagesPath.intervention.interventionsList,
    labelKey: 'programs',
    id: 'paths',
  });

  items.push({
    path: pagesPath.main.journal,
    labelKey: 'journal',
    id: 'journal',
  });

  return items;
});

/**
 * One prompt at a time, in this order: the legal consent (its own page), the renewal flow, the IFU
 * notice (device information), then the optional analytics consent. Each hands over to the next, so
 * two dialogs are never open at once (IPD-3216, IPD-3536).
 */
function raisePrompts(userData: UserResponse): void {
  if (isLegalConsentPending(userData)) {
    // The consent page owns the screen: the expiry watch sends the user there, and until the terms
    // and privacy policy are approved the backend refuses everything a dialog here would save.
  } else if (deviceConsentPending(userData)) {
    // The consent gate on the interventions list owns the screen; the notice waits for a later load.
  } else if (isContractExpired(userData)) {
    // The renewal flow owns the screen; what follows comes once they renew (see the watches below).
  } else if (userData.ifuUpdateAvailable === true) {
    showIfuUpdateModal.value = true;
  } else if (!userData.consents?.analyticsAnonymized) {
    // No record yet — show one-time modal for existing users
    showAnalyticsAnonymizedModal.value = true;
  }
}

const [fetchMe, meLoading, meErr] = useLoadingCb(
  async() => {
    const userData = await api.me.getMe();
    const shouldRedirect = syncBackendLanguage(userData.language);
    if (shouldRedirect) {
      // Set BEFORE returning so the template's :loading binding stays truthy
      // when useLoadingCb flips meLoading→false next; otherwise loading-error
      // would hide its spinner and briefly paint an empty page shell before
      // the browser processes the queued window.location.replace.
      isRedirecting.value = true;
      return {userData, homeContent: null, shouldRedirect};
    }

    // Fetched for every user, with or without a license and whatever page the license hides: it
    // carries the side panel's FAQ, glossary and crisis resources. A failure hides that panel rather
    // than taking down every page behind this one.
    const homeContent = await api.me.getHomeContent().catch((err: unknown) => {
      logger.error('Failed to load the support content:', err)();
      return null;
    });

    return {userData, homeContent, shouldRedirect};
  },
  res => {
    if (res.shouldRedirect) {
      // syncBackendLanguage already queued a window.location.replace inside
      // the async body — skip every reactive write so we don't briefly paint
      // partial state (e.g. German userData while i18n is still on the
      // pre-login locale) before the reload tears the page
      // down. The post-reload pass repopulates everything with the correct
      // locale.
      return;
    }
    const {userData, homeContent} = res;
    userStore.setUserData(userData);
    userStore.setHomeContent(homeContent);

    // Sync user preferences from backend
    if (userData.colorMode && Object.values(ColorScheme).includes(userData.colorMode as ColorScheme)) {
      userPreferencesStore.colorScheme = userData.colorMode as ColorScheme;
    }
    if (typeof userData.minFontSize === 'number') {
      userPreferencesStore.minFontSize = userData.minFontSize;
    }

    raisePrompts(userData);
  },
);

// Renewing releases the prompts the renewal flow held back, without waiting for a reload. They come
// once the user leaves that page, where the payment dialog may still be showing its confirmation, so
// two dialogs never stack.
let promptsHeldByRenewal = false;
watch(() => contractExpired.value, (isExpired, wasExpired) => {
  promptsHeldByRenewal = Boolean(wasExpired) && !isExpired;
});
watch(() => route.path, () => {
  if (promptsHeldByRenewal && userStore.userData) {
    promptsHeldByRenewal = false;
    raisePrompts(userStore.userData as UserResponse);
  }
});

function handleIfuAcknowledged(): void {
  if (!userStore.userData?.consents?.analyticsAnonymized) {
    showAnalyticsAnonymizedModal.value = true;
  }
}

onBeforeMount(async() => {
  await fetchMe();
});
</script>
<template>
  <loading-error id="main-base-page" :loading="meLoading || isRedirecting" :error="meErr" type="page-ignore-header">
    <global-page-template
      :enable-guide-drawer="true"
      :main-navigation-items="mainNavigationItems"
      :sidebar-menu-items="balmSidebarMenuItems"
    >
      <router-view v-slot="{Component}">
        <!-- Explore keeps its instance across navigation so filters, fetched rails and
             scroll position survive going into a card / View all and back. TopicPage is here for
             a different reason: it pages, so remounting would drop every page past the first and
             land the reader back at the top. It refreshes itself on activation past a staleness
             window, since a kept-alive instance would otherwise hold stale consumed markers. -->
        <keep-alive :include="['ExplorePage', 'TopicPage', 'PartnerContentPage']">
          <component :is="Component"/>
        </keep-alive>
      </router-view>
    </global-page-template>
    
    <!-- Instructions For Use notice: a newer IFU version has been published than the user has seen -->
    <ifu-update-modal
      v-if="showIfuUpdateModal"
      v-model="showIfuUpdateModal"
      @acknowledged="handleIfuAcknowledged"
    />

    <!-- Analytics anonymized consent modal for existing users without a record -->
    <analytics-anonymized-consent-modal
      v-if="showAnalyticsAnonymizedModal"
      v-model="showAnalyticsAnonymizedModal"
    />

    <!-- Onboarding Tour using Driver.js -->
    <onboarding-tour :enabled="true"/>

    <!-- Confirmations raised from anywhere in the signed-in app -->
    <snackbar-host/>
  </loading-error>
</template>
<style scoped lang="scss">

</style>
